import authentication from '@feathersjs/authentication-client'
import socketio from '@feathersjs/socketio-client'
import $ from 'jquery'
import _ from 'lodash'
import React from 'react'
import ReactDom from 'react-dom'
import ReactGA from 'react-ga'
import io from 'socket.io-client'
import * as Sentry from '@sentry/browser'

import featureTogglesContainer from '../shared/featureToggles'
import { TeamChatContextProvider } from './contexts/team-chat'
import { logger } from './lib/logging'
import { DEMO_USER_EMAIL } from '../tool/demo/export/constants'
import { isSyntheticTestAccount } from '../shared/util/test-utils/checkTestAccount'
import AppContext from './app-context'
import PageContainer from './common/page/page_container/page_container.jsx'
import pageInfo from './common/pageinfo'
import feathers from './feathers'
import Acl from './lib/acl.jsx'
import currency from './lib/currency.jsx'
import DateUtil from './lib/dateutil.jsx'
import IntlUtil from './lib/intlutil.jsx'
import storage from './lib/localstorage.jsx'
import { TeamContext } from './lib/teamContext'
import { getDefaultVatRateForTeam, isVatSupportedInCountry } from '../shared/util/vat'

import { errorHandler } from './library/errorHandler'
import { Auth0Authentication } from './library/authentication/auth0'
import { DemoAuthentication } from './library/authentication/demo'
import { LocalAuthentication } from './library/authentication/local'
import { SessionNotAuthenticated } from './library/authentication/sessionNotAuthenticated'

import { setupIntercomForUsers, setupIntercomForGuests, updateIntercom } from './lib/intercom.jsx'
import { NoTeamAvailableError } from '../shared/errors/NoTeamAvailableError'
import { SUPPORT_ACCOUNTS } from '../shared/constants'
import { getAppUrl } from './lib/getAppUrl'
import analyticsModule from './library/analytics'
import { ANALYTICS_EVENTS, ANALYTICS_FIELDS } from './library/analytics/constants'
import { isReviewApp } from '../shared/util/environment'

class App {
  constructor () {
    this._socket = null
    this._app = null
    this._country = 'NL'
    this._language = this._getDefaultLanguage()
    this._request = null
    this._pgCont = null
    this._isMobile = false
    this._isTablet = false
    this._bUserTask = false
    this._bHasRecurringInvoice = false
    this._bDevMode = false
    this._userTeams = []
    this.isDemo = false
    this.teamContext = new TeamContext(this)
  }

  get socket () {
    return this._socket
  }

  get feathers () {
    if (!this._app) {
      throw new Error('Feathersjs client has not been setup yet.')
    }

    return this._app
  }

  get feathersApp () {
    return this.feathers
  }

  get currency () {
    return this._currency
  }

  get request () {
    return this._request
  }

  get loggedInId () {
    return this.user ? this.user.id : null
  }

  get isLoggedIn () {
    return Boolean(this.user)
  }

  get loggedInUserTeam () {
    return this.teamContext.activeUserTeam
  }

  get isMobile () {
    return this._isMobile
  }

  get isTablet () {
    return this._isTablet
  }

  get isMobileOrTablet () {
    return this._isMobile || this._isTablet
  }

  get isDesktop () {
    return !this._isMobile && !this._isTablet
  }

  get title () {
    return 'Rodeo'
  }

  get hasUserTask () {
    return this._bUserTask
  }

  get hasRecurringInvoice () {
    return this._bHasRecurringInvoice
  }

  get pageContainer () {
    return this._pgCont
  }

  get user () {
    return this._user
  }

  get teamCountry () {
    return this._country
  }

  get projectSetting () {
    const team = this.getTeam()
    return team && team.projectSetting ? team.projectSetting : {}
  }

  get language () {
    return this._language
  }

  /**
   * Get the authentication strategy.
   */
  get authentication () {
    if (!this._authentication) {
      throw new Error('Authentication has not been setup yet.')
    }

    return this._authentication
  }

  /**
   * Get the active team.
   *
   * @returns {Object|null}
   */
  get activeTeam () {
    return this.teamContext.activeTeam
  }

  /**
   * Get the active team id.
   */
  get teamId () {
    return this.activeTeam ? this.activeTeam.id : null
  }

  /**
   * Get the active team.
   */
  getTeam () {
    return this.activeTeam
  }

  getUser () {
    return this.user
  }

  /**
   * Get the active team id.
   */
  getTeamId () {
    return this.teamId
  }

  /**
   * Get the available teams for the user.
   *
   * @returns {Object[]}
   */
  get availableTeams () {
    if (!this.teamContext.availableTeams) {
      throw new NoTeamAvailableError('Teams have not been set yet.')
    }

    return this.teamContext.availableTeams
  }

  canGenerateDemoData () {
    const user = this.user
    if (user && user.email) {
      const email = user.email.toLowerCase()
      return email === DEMO_USER_EMAIL
    }

    return false
  }

  canAccessDevMode () {
    const user = this.user
    if (user && user.email) {
      return user.email.toLowerCase().includes('@getrodeo.io')
    }

    return false
  }

  isDevModeEnabled () {
    return this._bDevMode
  }

  toggleDevMode () {
    this._bDevMode = !this._bDevMode
  }

  getDefaulRateMarkup () {
    const team = this.getTeam()
    return team && team.defaultRateMarkup ? team.defaultRateMarkup : 0
  }

  setTermsAndCondition (content) {
    const team = this.getTeam()
    team.termsAndCondition = content
    this.setTeam(team.id)
  }

  setDefaultRateMarkup (markup) {
    const team = this.getTeam()
    team.defaultRateMarkup = markup
    this.setTeam(team.id)
  }

  setProjectSetting (setting) {
    const team = this.getTeam()
    team.projectSetting = setting
    this.setTeam(team.id)
  }

  setPersonalSetting (setting) {
    const userTeam = this.loggedInUserTeam
    userTeam.personalSetting = setting
    this._app.set('userTeam', userTeam)
  }

  setUserTask (bUserTask) {
    this._bUserTask = bUserTask
  }

  setHasRecurringInvoice (bHasRecurringInvoice) {
    this._bHasRecurringInvoice = bHasRecurringInvoice
  }

  setPageContainer (pageCont) {
    this._pgCont = pageCont
  }

  setTeamCountry (country) {
    this._country = country
    let currCode = 'EUR'
    if (country === 'UK') {
      currCode = 'GBP'
    } else if (country === 'US') {
      currCode = 'USD'
    }

    currency.setCurrency(currCode)

    currency.setCountryFormat(country)
    DateUtil.setCountryFormat(country)
  }

  setLanguage (language) {
    if (language) {
      this._language = language.toLowerCase()
    }
  }

  changeLanguage (language) {
    this.setLanguage(language)
    IntlUtil.setIntl(null)
    IntlUtil.setup(this._language)
  }

  setTimezone (timezone) {
    if (timezone) {
      DateUtil.setTimezone(timezone)
    }
  }

  service (ident) {
    return this._app.service(ident)
  }

  get (name) {
    // Legacy: previously the config would be stored in localStorage.
    // Some code therefore expects to get a JSON serialized object when calling
    // app.get('config') and try to parse it, so we stringify it here to not break that.
    if (name === 'config') {
      return JSON.stringify(this.config)
    }

    return storage.getItem(name)
  }

  getAsObject (name) {
    return JSON.parse(storage.getItem(name))
  }

  // @deprecated: use this.config
  getConfig () {
    return this.config
  }

  clear (name) {
    storage.removeItem(name)
  }

  set (name, value) {
    storage.setItem(name, value)
  }

  setAsObject (name, value) {
    storage.setItem(name, JSON.stringify(value))
  }

  isTeamExpired () {
    const team = this.getTeam()
    if (team && team.subscription && team.subscription.isExpired) {
      return true
    }

    return false
  }

  isTrail () {
    const team = this.getTeam()
    if (team && team.subscription && team.subscription.status === 'trail') {
      return true
    }

    return false
  }

  isSupportUser () {
    return this.user && Object.values(SUPPORT_ACCOUNTS).includes(this.user.email)
  }

  getDefaultVat () {
    const team = this.getTeam()
    const teamVatRate = team && team.profile && team.profile.vatRate
    const teamCountry = team && team.profile && team.profile.country

    return getDefaultVatRateForTeam(teamCountry, teamVatRate)
  }

  isVatSupportedInCountry (country) {
    return isVatSupportedInCountry(country)
  }

  isTeamBlocked () {
    const team = this.getTeam()
    if (team && !team.active) {
      return true
    }

    return false
  }

  isQuickbookConnected () {
    const team = this.getTeam()
    if (team && team.quickbook && team.quickbook.isConnected) {
      return true
    }

    return false
  }

  // TODO: Can we remove these?
  debug (...args) {
    logger.debug(...args)
  }

  // TODO: Can we remove these?
  error (...args) {
    logger.error(...args)
  }

  setTeam (teamId) {
    return this.teamContext.switchToTeamById({ teamId, userId: this.user.id })
  }

  /**
   * Return the (authentication) module of the app.
   */
  getModule () {
    return 'user'
  }

  /**
   * Return the storage key for the JWT.
   */
  getJwtStorageKey () {
    if (this.isDemo) {
      return 'demoAuthJwt'
    }

    return `${this.getModule()}AuthJwt`
  }

  /**
   * Initialize the application.
   *
   * @param {object} Options
   */
  async init ({ isDemo = false } = {}) {
    this.isDemo = isDemo

    await this.initFeathers()
    await this.initConfig()
    await this.initRequest()
    await this.initAuth()
    await this.initSentryContext()

    if (isDemo) {
      await this.initDemo()
    }

    await this.initDevice()
    await IntlUtil.setup(this._language)
    await this.initHooks()
    await this.initGa()
    await this.initPage()
    await this.routePage()

    const team = this.getTeam()
    this.isSyntheticTestAccount = isSyntheticTestAccount(team?.name, team?.profile?.email)

    await this.triggerIntercom()
    await this.trackFirstPageView()
  }

  initFeathers () {
    this._socket = io('//', { transports: ['websocket'], upgrade: false })

    // Set up Feathers client side
    this._app = feathers.init()

    // Register socket.io
    this._app.configure(socketio(this._socket))

    // Set up authentication with a store to cache your auth token.
    this._app.configure(authentication({ storage, storageKey: this.getJwtStorageKey() }))

    // If the transport changes, you have to call authenticate() again.
    this._app.authentication.handleSocket(this._socket)

    this._app.hooks({
      before: (hook) => {
        logger.debug('Calling service', `[${hook.path}]`, hook)

        // Add the module to the query (app or console)
        hook.params.query = { ...(hook.params.query || {}), __module: this.getModule() }

        if (this.teamId && hook.path !== 'api/users/team' && hook.path !== 'api/team') {
          hook.params.query.teamId = this.teamId
        }
      },
      error: errorHandler(this)
    })
  }

  async initConfig () {
    const result = await this.service('/api/config').get(null)
    logger.debug('Setting config', result)
    this.config = result
  }

  initDevice () {
    this._isMobile = false
    this._isTablet = false

    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    ) {
      this._isMobile = true
    }
  }

  initAuth () {
    const { strategy } = this.config.authentication

    if (this.isDemo) {
      this._authentication = new DemoAuthentication(this)
    } else if (strategy === 'auth0') {
      this._authentication = new Auth0Authentication(this)
    } else if (strategy === 'local') {
      this._authentication = new LocalAuthentication(this)
    } else {
      throw new Error(`Invalid authentication strategy: [${strategy}]`)
    }

    return this.authentication.init().then((data) => {
      const { activeTeam } = data
      const onGuestPage = this.authentication.isGuestPage(window.location.pathname)
      const teamSelectPages = ['/login', '/team-select']
      // We don't care about guest pages when the demo is on
      if (this.isDemo) {
        return null
      // If the user doesn't have a team selected
      // we can't continue so we redirect them to the
      // login page so they can pick a team.
      } else if (!activeTeam && !onGuestPage && !teamSelectPages.includes(window.location.pathname)) {
        logger.debug('User is authenticated, but does not have a team, redirecting to team-select.')
        return this.redirect('/team-select')
      } else if (onGuestPage) {
        logger.debug('User is on a guest page, redirecting to dashboard.')
        return this.redirect('/')
      }
    }).catch((error) => {
      if (this.isDemo) {
        // If team was deleted we reload a new demo environment
        if (error instanceof NoTeamAvailableError) {
          this.logout('/')
        }

        return null
      } else if (error instanceof SessionNotAuthenticated) {
        logger.debug('No active user session found.')

        if (this.authentication.isAuthenticatedPage(window.location.pathname)) {
          logger.debug('Page requires authorization, so redirecting to login.')
          return this.redirect('/login')
        }
      } else if (error instanceof NoTeamAvailableError) {
        // If, for whatever reason, we can't get the team context we log the user out.
        // This could for example be caused by a teamId set in localstorage that is
        // invalid or no longer exists.
        return this.logout()
      } else {
        // overall when we have an error, we should logout from current user
        logger.error('An error happend in initAuth', error)
        return this.logout()
      }
    })
  }

  initSentryContext () {
    if (this.teamContext && this.teamContext.activeTeam) {
      const team = this.teamContext.activeTeam
      Sentry.setExtra('team_id', team.id)
    }

    if (this.user) {
      Sentry.setUser({ id: this.user.id })
    }

    if (this.teamContext && this.teamContext.activeUserTeam) {
      const userTeam = this.teamContext.activeUserTeam
      Sentry.setExtra('userteam', {
        id: userTeam.id,
        teamId: userTeam.teamId,
        userId: userTeam.userId,
        language: userTeam.language,
        timezone: userTeam.timezone,
        roles: userTeam.roles,
        roleType: userTeam.roleType
      })
    }
  }

  initDemo () {
    // If we are already logged in, we don't need to setup the demo.
    if (this.isLoggedIn) {
      return Promise.resolve()
    }

    logger.debug('Setting up demo config...')

    // For demo team, show loader until loading all demo data
    this._request.pageIdent = 'demoimport'

    const country = $('#root').data('country') || 'NL'

    // logic is implemented in the page_demoimport, where we show loading to the user
    // so here we need to set language to show loading text in correct language

    // TODO: it is a temporary change for setting language from country.
    // We should fix language and locale inconsistency in the whole web app
    const countryToLanguageMapping = {
      NL: 'nl',
      DE: 'de',
      US: 'en',
      UK: 'en'
    }

    this.setLanguage(
      countryToLanguageMapping[country] || countryToLanguageMapping.NL
    )

    return Promise.resolve()
  }

  triggerIntercom () {
    const appId = this.config.intercom.appId

    // Don't set up intercom for support users or review apps
    if (this.isSupportUser() || isReviewApp()) {
      return
    }

    // Don't set up intercom if there is no appId set.
    if (!appId) {
      return
    }

    // Don't set up intercom for console.
    if (this.getModule() === 'console') {
      return
    }

    if (!this.isLoggedIn || this.isDemo) {
      return setupIntercomForGuests({ appId })
    }

    const team = this.getTeam()

    // Don't set up intercom for datadog synthetic tests.
    if (this.isSyntheticTestAccount) {
      return
    }

    const user = this.user
    const userTeam = this.loggedInUserTeam

    if (!team || !userTeam) {
      return
    }

    return setupIntercomForUsers({ appId, userTeam, team, user })
  }

  initHooks () {
    if (this.isLoggedIn) {
      this._app.mixins.push(service => {
        service.mixin({
          before (before) {
            return this.hooks({ before })
          }
        })

        if (typeof service.before !== 'function') {
          // Left empty intentionally
        } else {
          service.before(this.populateParams.bind(this))
        }
      })
    }
  }

  initGa () {
    const config = this.config
    if (config && config.tracker.ga.enabled) {
      ReactGA.initialize(config.tracker.ga.code)
    }
  }

  trackFirstPageView () {
    analyticsModule.getMixpanelInstance().track(ANALYTICS_EVENTS.pageView, { [ANALYTICS_FIELDS.page]: this._request.pageIdent })
  }

  callGaEventPageview () {
    const config = this.config
    if (config && config.tracker.ga.enabled) {
      ReactGA.ga('send', 'pageview', window.location.href)
    }
  }

  initRequest () {
    const pageEl = $('#root')

    this._request = {
      pageIdent: pageEl.data('ident'),
      param1: pageEl.data('param1'),
      param2: pageEl.data('param2'),
      param3: pageEl.data('param3')
    }

    this._currency = 'EUR'
  }

  populateParams (hook) {
    hook.params.query = Object.assign({}, hook.params.query, {
      teamId: this.getTeamId()
    })
  }

  unAuthorizedPages () {
    return [
      'login',
      'reset',
      'signup',
      'signupteam',
      '404Error',
      'blocked',
      'demo',
      'forgot-password',
      'signupconfirm',
      'emailunsubscribe',
      'setting-paymentfailed',
      'demoimport'
    ]
  }

  initPage () {
    try {
      if (this.isLoggedIn) {
        window.addEventListener(
          'popstate',
          evt => {
            if (evt.state) {
              this.updatePageRequest(
                evt.state.pageIdent,
                evt.state.title,
                evt.state.param1,
                evt.state.param2,
                evt.state.param3
              )
            } else {
              this.updatePageRequest('dashboard', '', 'Dashboard')
            }
          },
          false
        )
      } else {
        window.removeEventListener('popstate')
      }
    } catch (e) {
      // Left empty intentionally
    }
  }

  routePage () {
    const pageIdent = this._request.pageIdent
    logger.debug('Routing page:', pageIdent)

    if (this.isDemo && (pageIdent === 'login' || pageIdent === 'signup' || pageIdent === 'signupteam' || pageIdent === 'team-select')) {
      this.redirect('/')
    } else if (_.indexOf(this.unAuthorizedPages(), this._request.pageIdent) >= 0) {
      this.showPage()
    } else {
      if (this.isMobile && this._request.pageIdent !== 'emailconfirm' && this._request.pageIdent !== 'team-select') {
        this._request.pageIdent = 'time-mobile'
      }

      if (
        Acl.isPageAllowed(this._request.pageIdent, this._request.param1, this._request.param2, this._request.param3) &&
            (this._request.pageIdent === 'setting-subscription' || (!this.isTeamExpired() && !this.isTeamBlocked()))
      ) {
        this.showPage()
      } else {
        // User dont have privielges to view the current page, show only dashboard page
        this._request = {
          pageIdent: 'dashboard',
          param1: null,
          param2: null,
          param3: null
        }
        this.showPage()
      }
    }
  }

  showPage () {
    window.rootApp = document.getElementById('root')

    const context = {
      featureToggles: this.getFeatureToggles(),
      isSubscriptionV2: this.isSubscriptionV2
    }

    ReactDom.render(
      <AppContext.Provider value={context}>
        <TeamChatContextProvider>
          <PageContainer />
        </TeamChatContextProvider>
      </AppContext.Provider>,
      window.rootApp
    )

    this.callGaEventPageview()
  }

  logout (url) {
    analyticsModule.getMixpanelInstance().reset()
    analyticsModule.setMixpanelInstance(null)
    return this.authentication.logout(url)
  }

  redirect (url) {
    url = this.getAppUrl(url)
    logger.debug('Redirecting to', url)
    window.location.href = url
    // calling window.location.href does not stop script execution
    // so to allow code to "wait" for the redirect to happen we return
    // a promise that never resolves.
    return new Promise(() => {})
  }

  getAppUrl (inputUrl) {
    return getAppUrl(inputUrl)
  }

  getPageTitle (newTitle, extraInfo) {
    const rodeo = this.title
    const demo = 'Demo'
    const divider = ' • '

    const defaultTitle = [extraInfo, newTitle, rodeo].filter(Boolean).join(divider)

    if (this.isDemo) {
      return [demo, defaultTitle].join(divider)
    }

    return defaultTitle
  }

  changePage (pageIdent, param1, param2, param3) {
    if (
      Acl.isPageAllowed(pageIdent, param1, param2, param3) &&
      (pageIdent === 'setting-subscription' ||
        pageIdent === 'setting-paymentfailed' ||
        (!this.isTeamExpired() && !this.isTeamBlocked()))
    ) {
      try {
        const info = this.getPageInfo(pageIdent)
        if (info) {
          let url = info.url
          if (param1) {
            url = url.replace(/:param1/, param1)
          }

          if (param2) {
            url = url.replace(/:param2/, param2)
          }

          if (param3) {
            url = url.replace(/:param3/, param3)
          }

          url = this.getAppUrl(url)

          const title = this.getPageTitle(info.title)

          history.pushState(
            {
              pageIdent,
              param1,
              param2,
              param3,
              url,
              title
            },
            title,
            url
          )
          this.updatePageRequest(pageIdent, title, param1, param2, param3)
        }
      } catch (e) {
        // Left empty intentionally
      }
    } else {
      this.updatePageRequest('dashboard', '', 'Dashboard')
    }
  }

  getPageInfo (pageIdent) {
    if (pageInfo[pageIdent] !== undefined) {
      return pageInfo[pageIdent]
    }

    return null
  }

  updatePageRequest (pageIdent, title, param1, param2, param3) {
    document.title = title

    if (this._pgCont) {
      this._request.pageIdent = pageIdent
      this._request.param1 = param1
      this._request.param2 = param2
      this._request.param3 = param3
      this._pgCont.reloadPage()

      analyticsModule.getMixpanelInstance().track(ANALYTICS_EVENTS.pageView, { [ANALYTICS_FIELDS.page]: pageIdent })
      this.callGaEventPageview()
      updateIntercom()
    }
  }

  updatePageTitle (pageIdent, objectName) {
    const info = this.getPageInfo(pageIdent)
    if (info) {
      document.title = this.getPageTitle(info.title, objectName)
    }
  }

  getOptions (value, splitter) {
    const idx = value.indexOf(splitter)
    const opts = []
    if (idx > 0) {
      opts.push(value.substr(0, idx))
      opts.push(value.substr(idx + 1))
    }

    return opts[1]
  }

  getFeatureToggles = () => {
    const { featureToggles } = this.getConfig()
    return featureToggles
  }

  isSubscriptionV2 = country => {
    const featureToggles = this.getFeatureToggles()
    const toggles = featureTogglesContainer(featureToggles)
    return toggles.useNewSubscriptionServiceForCountryCode(country)
  }

  _getDefaultLanguage () {
    let lang = $('#root').data('lang')
    if (lang !== 'en' && lang !== 'nl' && lang !== 'de' && lang !== 'us') {
      lang = 'en'
    }

    return lang
  }
}

export { App }
export default new App()
